// UpdateBanner v2 — z-index fix + invalidateQueries after login
import { useQuery, useMutation } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../lib/api'
import { useAuth } from '../auth/AuthProvider'

interface UpdateInfo {
  current: string
  latest: string
  has_update: boolean
  release_notes?: string
  critical?: boolean
}

export function UpdateBanner() {
  const [dismissed, setDismissed] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<string | null>(null)
  const { t } = useTranslation()
  const { user } = useAuth()

  const { data } = useQuery<UpdateInfo>({
    queryKey: ['update-check'],
    queryFn: () => apiFetch('/api/system/update-check/'),
    refetchInterval: 1000 * 60 * 5,   // alle 5 Minuten
    staleTime: 1000 * 60 * 2,        // 2 Minuten Cache
    retry: 1,
    enabled: !!user,
  })

  const triggerUpdate = useMutation({
    mutationFn: () => apiFetch<{ job_id: string }>('/api/system/trigger-update/', { method: 'POST' }),
    onSuccess: (resp) => {
      setJobId(resp.job_id)
      setJobStatus('running')
    },
  })

  // Poll update status — reload when done or when server restarts
  useEffect(() => {
    if (!jobId || jobStatus !== 'running') return
    let failCount = 0
    const interval = setInterval(async () => {
      try {
        const resp = await apiFetch<{ status: string }>(`/api/system/update-status/${jobId}/`)
        failCount = 0
        setJobStatus(resp.status)
        if (resp.status === 'success') {
          clearInterval(interval)
          setTimeout(() => window.location.reload(), 3000)
        } else if (resp.status === 'failed') {
          clearInterval(interval)
        }
      } catch {
        failCount++
        // Server unreachable 3x = container restarting = update done
        if (failCount >= 3) {
          clearInterval(interval)
          setJobStatus('success')
          setTimeout(() => window.location.reload(), 5000)
        }
      }
    }, 5000)
    return () => clearInterval(interval)
  }, [jobId, jobStatus])

  if (!data?.has_update || dismissed) return null

  if (jobStatus === 'running') {
    return (
      <div className="fixed top-0 start-0 end-0 bg-[var(--brand-primary)] text-[var(--brand-on-primary)] px-4 py-3 text-center text-sm" style={{ zIndex: 99999 }}>
        {t('common.updateInstalling', 'Update wird installiert... Bitte warten.')}
      </div>
    )
  }

  if (jobStatus === 'success') {
    return (
      <div className="fixed top-0 start-0 end-0 bg-green-600 text-white px-4 py-3 text-center text-sm" style={{ zIndex: 99999 }}>
        {t('common.updateSuccess', 'Update erfolgreich! Seite wird neu geladen...')}
      </div>
    )
  }

  if (jobStatus === 'failed') {
    return (
      <div className="fixed top-0 start-0 end-0 bg-red-600 text-white px-4 py-3 flex items-center justify-between text-sm" style={{ zIndex: 99999 }}>
        <span>{t('common.updateFailed', 'Update fehlgeschlagen. Alte Version laeuft weiter.')}</span>
        <button onClick={() => { setJobStatus(null); setJobId(null) }} className="text-white/70 hover:text-white ms-4">
          {t('common.dismiss', 'Schliessen')}
        </button>
      </div>
    )
  }

  return (
    <div className={`fixed top-0 start-0 end-0 px-4 py-2 flex items-center justify-between text-sm ${data.critical ? 'bg-red-600 text-white' : 'bg-[var(--brand-primary)] text-[var(--brand-on-primary)]'}`} style={{ zIndex: 99999 }}>
      <span>
        {data.critical
          ? t('common.criticalUpdate', 'Kritisches Update')
          : t('common.updateAvailable', 'Update verfuegbar')}
        : v{data.latest}
        {data.release_notes ? ` — ${data.release_notes}` : ''}
      </span>
      <div className="flex gap-2 items-center">
        <button
          onClick={() => setDismissed(true)}
          className="text-[var(--brand-on-primary)]/70 hover:text-[var(--brand-on-primary)] text-sm"
        >
          {t('common.later', 'Spaeter')}
        </button>
        {(user?.role === 'admin' || (user?.role as any)?.name === 'admin') && (
          <button
            onClick={() => triggerUpdate.mutate()}
            disabled={triggerUpdate.isPending}
            className="bg-white dark:bg-zinc-900 text-[var(--brand-primary-2)] px-3 py-1 rounded text-sm font-medium hover:bg-[var(--brand-surface-soft)] disabled:opacity-50"
          >
            {triggerUpdate.isPending
              ? t('common.updateStarting', 'Wird gestartet...')
              : t('common.installNow', 'Jetzt installieren')}
          </button>
        )}
      </div>
    </div>
  )
}
